import { Box, ClickAwayListener, Grid, Grid2, List, Radio } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Switch from 'components/form/Switch'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import Popup from 'components/popup/Popup'
import { SelectableItem } from './SelectItemWithSearchPopup'
import PopupSection from 'components/popup/PopupSection'
import { t, use } from 'i18next'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import { MiscText } from 'components/login/styled'
import { IEventDetails } from 'hooks/useEventDetails'
import { Tier, highestActiveTier } from 'utils/api/accounts'
import { isUserCanOperateEvents } from 'utils/api/groups'
import i18n from 'i18n'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { set } from 'date-fns'
import { Model } from 'types/model'
import { CheckmarkIcon } from 'assets/icons'

import { localGroupById } from 'services/db/Queries/GroupQueries'
import { removePrefix } from 'utils/object'
import { TierFeatures } from 'utils/api/group_state'
import { is24meApp } from 'utils/appType'
import Button from 'components/buttons/Button'
import { isEqual } from 'utils/object'

export interface CalendarSectionItem {
  title: string
  items: SelectableItem[]
  sectionIcon?: ReactNode
  availableMultiMode?: boolean
}

interface CalendarSelectPopupInterface {
  calendars: CalendarSectionItem[]
  open?: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  eventHook: IEventDetails | undefined
  group: GroupDoc | undefined
  onCalendarPick: (calendar: SelectableItem) => void
  SupplementaryGroupsIDs: string[]
  account: Model<AccountDoc> | AccountDoc
  onlyCalendarPick?: boolean
}

function CalendarSelectPopupContent(props: CalendarSelectPopupInterface) {
  const theme = useTheme()
  const [supplementaryMode, setSupplementaryMode] = useState(
    props.SupplementaryGroupsIDs.length > 0
  )

  const [virtualSupplementaryGroupsIDs, setVirtualSupplementaryGroupsIDs] =
    useState<string[]>(props.SupplementaryGroupsIDs)

  useEffect(() => {
    setVirtualSupplementaryGroupsIDs(props.SupplementaryGroupsIDs)
  }, [props.SupplementaryGroupsIDs])

  function performSuppModeChange(enabled: boolean) {
    setSupplementaryMode(enabled)
    if (enabled) {
      // do nothing
    } else {
      props.eventHook?.setSupplementaryGroupIds([])
    }
  }

  const onSuppModeChanged = (event: ChangeEvent<HTMLInputElement>) => {
    performSuppModeChange(event.target.checked)
  }
  const isUserAllowedToAddEvents = isUserCanOperateEvents(
    props.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )
  const convertedGroup = isConvertedGroupId(props.eventHook?.groupId)
  const isSyncNotPossible = convertedGroup || !isUserAllowedToAddEvents
  const [itemGroup, setItemGroup] = useState<GroupDoc | undefined>(undefined)

  // Create a ref for the item you want to scroll to
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Scroll to the item when the component mounts
    ;(itemRef.current as any)?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    })
  }, [])

  useEffect(() => {
    localGroupById(props.eventHook?.groupId ?? '').then((group) => {
      if (!group) return
      setItemGroup(group)
    })
  }, [props.eventHook?.groupId])

  const isDirty = !isEqual(
    virtualSupplementaryGroupsIDs.sort(),
    props.SupplementaryGroupsIDs.sort()
  )

  useEffect(() => {
    if (!props.open) {
      setVirtualSupplementaryGroupsIDs(props.SupplementaryGroupsIDs)
    }
  }, [props.open])

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        maxWidth: '350px',
        maxHeight: '450px',
        fontSize: '14px'
      }}
    >
      <Grid container direction={'column'}>
        {!props.onlyCalendarPick && (
          <Grid
            container
            direction={'column'}
            sx={{
              paddingLeft: 5,
              paddingRight: 5,
              paddingBottom: 2,
              backgroundColor: 'var(--color-surface-hover)',
              position: 'sticky',

              zIndex: 10,
              top: 0
            }}
          >
            {isSyncNotPossible || is24meApp() ? (
              <></>
            ) : (
              <div
                style={{
                  backgroundColor: 'var(--color-surface-active)',
                  borderRadius: 8,
                  padding: theme.spacing(1, 4)
                }}
              >
                <PopupSectionRow
                  title={t('syncEventsAcrossCalendars')}
                  neededTier={
                    itemGroup?.OwnerID ===
                    LocalStorage.get(LocalStorageKey.USER_ID)
                      ? Tier.BUSINESS_PLUS
                      : Tier.FREE
                  }
                  tierFeature={TierFeatures.SYNC_WITH_CALENDARS}
                  account={props.account}
                  action={performSuppModeChange.bind(null, true)}
                  value={
                    <Switch
                      onChange={onSuppModeChanged}
                      checked={supplementaryMode}
                    />
                  }
                />
                <MiscText
                  fontSize={'13px'}
                  fontWeight={'400 !important'}
                  lineHeight={1.5}
                  letterSpacing={'0.02em'}
                >
                  {t('updatesToThisEvent')}
                </MiscText>
              </div>
            )}
          </Grid>
        )}
        <List
          sx={{
            width: '100%'
          }}
        >
          {props.calendars
            .filter((section) => {
              if (!supplementaryMode) return true

              return section.availableMultiMode
            })
            .map((item, index) => {
              return SectionWithCalendars(
                item,
                index,
                supplementaryMode
                  ? virtualSupplementaryGroupsIDs
                  : props.SupplementaryGroupsIDs,
                supplementaryMode ? undefined : props.onCalendarPick,
                supplementaryMode,
                props.eventHook,
                isSyncNotPossible,
                props.account,
                itemGroup,
                itemRef,
                setVirtualSupplementaryGroupsIDs
              )
            })}
        </List>
        {supplementaryMode && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              paddingTop: '0.5rem',
              paddingBottom: '0.1rem',
              background: 'var(--color-surface-hover)',
              borderTop: `1px solid ${theme.palette.border.primary}`,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              disabled={!isDirty}
              size="xs"
              onClick={() => {
                props.eventHook?.setSupplementaryGroupIds(
                  virtualSupplementaryGroupsIDs
                )
                props.setOpen(false)
              }}
              width="100%"
            >
              {t('apply')}
            </Button>
          </Box>
        )}
      </Grid>
    </Box>
  )
}

function SectionWithCalendars(
  sectionItem: CalendarSectionItem,
  index: number,
  SupplementaryGroupsIDs: string[],
  onCalendarPick?: (item: SelectableItem) => void,
  multiMode?: boolean,
  eventHook?: IEventDetails,
  isSyncNotPossible?: boolean,
  account?: Model<AccountDoc> | AccountDoc,
  group?: GroupDoc,
  itemRef?: any,
  onSupplementaryChange?: (ids: string[]) => void
) {
  function addOrRemoveItem(item: SelectableItem) {
    if (
      group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID) &&
      highestActiveTier(account) < Tier.BUSINESS_PLUS
    ) {
      return
    }

    if (item.notClickable === true) {
      return
    }

    if (group?._id === item.id) {
      return
    }

    if (!multiMode) return

    if (!SupplementaryGroupsIDs.includes(item.id ?? '')) {
      const newIds = [...SupplementaryGroupsIDs, item.id ?? '']
      if (onSupplementaryChange) {
        onSupplementaryChange(newIds)
      } else {
        eventHook?.setSupplementaryGroupIds(newIds)
      }
    } else {
      const newIds = SupplementaryGroupsIDs.filter((id) => id !== item.id)
      if (onSupplementaryChange) {
        onSupplementaryChange(newIds)
      } else {
        eventHook?.setSupplementaryGroupIds(newIds)
      }
    }
  }

  return (
    <PopupSection
      key={sectionItem.title}
      title={sectionItem.title}
      titleIcon={sectionItem.sectionIcon}
    >
      <List
        sx={{
          width: '100%',
          paddingTop: 0
        }}
      >
        {sectionItem.items.map((item, index) => {
          const isSelected =
            SupplementaryGroupsIDs.includes(item.id ?? '') ||
            group?._id === item.id
          return (
            <Grid2
              ref={
                !multiMode &&
                (item.id === removePrefix(eventHook?.groupId ?? '') ||
                  item.id === eventHook?.thirdPartyId)
                  ? itemRef
                  : null
              }
              width={'100%'}
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              sx={{
                cursor: 'pointer',
                backgroundColor:
                  (item.notClickable && !multiMode && item.id !== group?._id) ||
                  (item.notClickable && multiMode)
                    ? 'rgba(0, 0, 0, 0.1)'
                    : '',
                padding: '0px 8px',
                margin: '4px 0px',
                borderRadius: '8px',
                minHeight: multiMode ? '52px' : '30px',
                '&:hover': {
                  backgroundColor: !(
                    (item.notClickable &&
                      !multiMode &&
                      item.id !== group?._id) ||
                    (item.notClickable && multiMode)
                  )
                    ? 'var(--color-surface-hover)'
                    : undefined
                }
              }}
              key={item.id}
            >
              <Grid
                onClick={
                  item.notClickable
                    ? undefined
                    : multiMode
                      ? addOrRemoveItem.bind(null, item)
                      : onCalendarPick?.bind(null, item)
                }
                container
                display={'flex'}
                direction={'column'}
              >
                <Box
                  display={'flex'}
                  color={'var(--color-text-primary)'}
                  flexDirection={'row'}
                  gap={1}
                  id={`change-calendar-button-${item.title}`}
                >
                  {item.title}
                </Box>
                {multiMode ? (
                  <MiscText
                    fontSize={'13px'}
                    fontWeight={'400 !important'}
                    lineHeight={1.5}
                    letterSpacing={'0.02em'}
                    color={item.subTitleColor ?? 'rgba(0, 0, 0, 0.4)'}
                  >
                    {item.notClickableReason
                      ? item.notClickableReason
                      : isSelected
                        ? t('tap_unselect')
                        : t('tap_select')}
                  </MiscText>
                ) : (
                  <></>
                )}
              </Grid>

              {isSyncNotPossible || !multiMode ? (
                item.id === eventHook?.getValues('ThirdPartyID') ||
                item.id === eventHook?.groupId ? (
                  <CheckmarkIcon style={{ marginRight: 4 }} />
                ) : (
                  item.rightIcon
                )
              ) : multiMode && sectionItem.availableMultiMode ? (
                <Radio
                  sx={{ padding: 1 }}
                  checked={isSelected}
                  disabled={item.notClickable === true}
                  onClick={addOrRemoveItem.bind(null, item)}
                />
              ) : (
                <></>
              )}
            </Grid2>
          )
        })}
      </List>
    </PopupSection>
  )
}

export default function CalendarSelectPopup(
  props: CalendarSelectPopupInterface
) {
  return (
    <Popup
      setOpen={props.setOpen}
      open={props.open}
      width="350px"
      content={<CalendarSelectPopupContent {...props} />}
      placement={i18n.dir() === 'ltr' ? 'right' : 'left'}
      fallbackPlacements={
        i18n.dir() === 'ltr' ? ['right', 'left', 'top'] : ['left', 'top']
      }
    >
      {props.children}
    </Popup>
  )
}
